<template>
  <MetaHead :title="wtGeneral('navigation', 'setup')" />
  <ProjectHeader :meta="projectBase.projectMeta.value" :hasTabBar="roles?.length > 1">
    <template #tabBar v-if="roles?.length > 1">
      <div class="core-tab-bar-inner">
        <button
          v-for="(role, index) in roles"
          :key="role"
          type="button"
          class="core-tab-bar-item"
          :class="{ active: currentTabIndex === index }"
          @click="onTabClick(index)">
          <span class="core-tab-bar-label">{{ wtGeneral('roleParts', role) }}</span>
        </button>
      </div>
    </template>
  </ProjectHeader>

  <div class="relative core-wrapper md:px-12 lg:px-24" v-if="currentCompanyType">
    <ProjectSetupPageContent ref="componentSetup" :companyType="currentCompanyType" :role="roles[currentTabIndex]" />
  </div>
  <RolePopupNotification :role="roles[currentTabIndex]" />
  <ProjectResults :projectName="projectBase.projectName.value" @onSaveProject="onSaveProject"></ProjectResults>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import ProjectHeader from '@/components/partials/navigation/ProjectHeader.vue'
import ProjectResults from '@/components/partials/display/ProjectResults.vue'
import MetaHead from '@/components/partials/display/MetaHead.vue'
import projectPageBase from '@/base/projectPageBase'
import ProjectSetupPageContent from '@/views/project/ProjectSetupPageContent.vue'
import RolePopupNotification from '@/components/partials/display/RolePopupNotification.vue'
import type { CompanyType } from '@/views/project/ProjectSetupPageContent.vue'
import { useRoute, useRouter } from 'vue-router'
import { useWidgetText } from '@/helpers/useWidgetText'
import { useProjectStore } from '@/stores/project'

const { wt: wtGeneral } = useWidgetText('widget-texts-general')

const route = useRoute()
const router = useRouter()
const projectStore = useProjectStore()

const componentSetup = ref<null | InstanceType<typeof ProjectSetupPageContent>>(null)

const projectBase = projectPageBase.useProjectPageBase() // part is set inside the component
const roles = projectBase.projectValidRoles
const currentTabIndex = ref(0)
const roleCompanyTypeMap: Record<string, CompanyType> = {
  ARCHITECT: 'architect',
  LANDSCAPE_ARCHITECT: 'landscape_architect',
  CIVIL_ENGINEER: 'civil_engineer',
  CIVIL_ENGINEER_EXCAVATION: 'civil_engineer_excavation',
  ENGINEER: 'engineer',
  ELECTRICAL: 'electrical',
  HEATING_COOLING: 'heating_cooling',
  VENTILATION_AC: 'ventilation_ac',
  HVAC: 'hvac',
  SANITARY: 'sanitary',
  BUILDING_AUTOMATION: 'building_automation',
  SPECIALIST_COORDINATION: 'specialist_coordination',
}
const currentCompanyType = computed(() => roleCompanyTypeMap[roles.value[currentTabIndex.value]] ?? null)

function load() {
  if (route?.params?.currentTabIndex) {
    const index = parseInt(
      Array.isArray(route.params.currentTabIndex) ? route.params.currentTabIndex[0] : route.params.currentTabIndex,
      10
    )
    currentTabIndex.value = roles.value[index] ? index : 0
  } else {
    const activeRoleIndex = roles.value.indexOf(projectStore.getActiveProjectRole ?? '')
    currentTabIndex.value = activeRoleIndex === -1 ? 0 : activeRoleIndex
  }

  const activeRole = roles.value[currentTabIndex.value]
  if (activeRole) {
    projectStore.setActiveProjectRole(activeRole)
  }
}

function onTabClick(index: number): void {
  componentSetup.value?.syncCurrentFormToProject()
  router.push({ name: 'setup-project-view', params: { currentTabIndex: index } })
}

async function onSaveProject(): Promise<void> {
  await componentSetup.value?.onSaveProject()
}
onMounted(() => {
  projectBase.initCalculators()
  load()
})
watch(
  () => route.params.currentTabIndex,
  () => load()
)
</script>
